"use client";

import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddTransaction = () => {
  const today = new Date();

  const [merchants, setMerchants] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    transactions_type: "debit",
    transactions_date: today.toISOString().split("T")[0],
    transactions_id: "",
    transactions_month: today.getMonth() + 1,
    transactions_year: today.getFullYear(),
    transactions_amount: "",
    transactions_merchant_name: "",
    transactions_comments: "",
  });

  /* ================= FETCH MERCHANTS ================= */
  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const res = await axios.get(
        "https://logicx.premierlam.com/api/get-pixel-transactions-merchants",
        {
          headers: { authorization: localStorage.getItem("accessToken") },
        }
      );
      setMerchants(res.data.data || []);
    } catch {
      toast.error("Failed to load merchants");
    }
  };

  /* ================= HELPERS ================= */
  const formatToApiDate = (date) => {
    const d = new Date(date);
    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-")
      .toLowerCase();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      await axios.post(
        "https://logicx.premierlam.com/api/add-pixel-main-transactions",
        {
          ...formData,
          transactions_date: formatToApiDate(formData.transactions_date),
        },
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Transaction added successfully!");
      window.location.href = "/all-transaction";
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add transaction");
    } finally {
      setIsSubmitting(false);
    }
  };

  const merchantOptions = merchants.map((m) => ({
    value: m.main_transactions_merchant_name,
    label: m.main_transactions_merchant_name,
  }));

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h1 className="text-2xl font-semibold mb-6">
              Add Transaction
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* TYPE */}
                <div>
                  <label className="block text-sm mb-1">Type</label>
                  <select
                    name="transactions_type"
                    value={formData.transactions_type}
                    onChange={handleChange}
                    className="w-full border px-3 py-2 rounded"
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>

                {/* DATE */}
                <div>
                  <label className="block text-sm mb-1">Date</label>
                  <input
                    type="date"
                    name="transactions_date"
                    value={formData.transactions_date}
                    onChange={handleChange}
                    className="w-full border px-3 py-2 rounded"
                    required
                  />
                </div>

                {/* TRANSACTION ID */}
                <div>
                  <label className="block text-sm mb-1">
                    Transaction ID
                  </label>
                  <input
                    type="text"
                    name="transactions_id"
                    value={formData.transactions_id}
                    onChange={handleChange}
                    className="w-full border px-3 py-2 rounded"
                    required
                  />
                </div>

                {/* MONTH */}
                <div>
                  <label className="block text-sm mb-1">Month</label>
                  <select
                    name="transactions_month"
                    value={formData.transactions_month}
                    onChange={handleChange}
                    className="w-full border px-3 py-2 rounded"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>

                {/* YEAR */}
                <div>
                  <label className="block text-sm mb-1">Year</label>
                  <select
                    name="transactions_year"
                    value={formData.transactions_year}
                    onChange={handleChange}
                    className="w-full border px-3 py-2 rounded"
                  >
                    {[2023, 2024, 2025].map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                {/* AMOUNT */}
                <div>
                  <label className="block text-sm mb-1">Amount</label>
                  <input
                    type="number"
                    name="transactions_amount"
                    value={formData.transactions_amount}
                    onChange={handleChange}
                    className="w-full border px-3 py-2 rounded"
                    required
                  />
                </div>

                {/* MERCHANT */}
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">
                    Merchant
                  </label>
                  <Select
                    options={merchantOptions}
                    onChange={(opt) =>
                      setFormData((p) => ({
                        ...p,
                        transactions_merchant_name: opt?.value || "",
                      }))
                    }
                    placeholder="Select Merchant"
                  />
                </div>

                {/* COMMENTS */}
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">Comments</label>
                  <textarea
                    name="transactions_comments"
                    value={formData.transactions_comments}
                    onChange={handleChange}
                    rows="3"
                    className="w-full border px-3 py-2 rounded"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="px-4 py-2 bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-orange-500 text-white rounded"
                >
                  {isSubmitting ? "Saving..." : "Add Transaction"}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTransaction;
